import { fetchRecommendProductListApi } from './api';
import { getMediaImageUrl } from '../../utils/img';
import { getProductPriceLabel } from '../../utils/getPrice';
import getTranslateModel from '../../utils/translateModel';

import {
  pageType,
  defaultLayoutStyle,
  e2eIds,
  classes,
  deviceType,
  deviceBreakpoint,
  layoutMode,
} from './constants';

import intersectionObserver from '../../utils/intersectionObserver';
import { getNgTrackerService } from '../../service/trackerService';
import {
  sendRecommendItem,
  sendViewRelatedProducts,
  sendProductImpression,
  sendProductClick,
} from './tracking';

const {
  cartRelatedProducts,
  cartRelatedProductsLoading,
  cartRelatedProductsList,
  cartRelatedProductsHide,
  cartRelatedProductsHorizontalScroll,
  hasCartRelatedProductsHorizontalScroll,
} = classes;

export class CartLazyLoadRelatedProducts extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-ids'];
  }

  constructor() {
    super();
    // attribute affect
    this.productIds = null;
    this.allowEmptyProductIds = false;

    this.observer = null;
    this.productObserver = null;
    this.mediaQuery = null;
    this.mediaQueryListener = null;
    this.trackerService = null;

    // data storage
    this.productIdsDataMap = new Map();
    this.isSentViewedEventProductIdsSet = new Set();
  }

  connectedCallback() {
    this.initAttributes();
    this.initObserver();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-ids' && oldValue !== newValue && newValue) {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.productObserver?.disconnect();
      this.productObserver = null;
      this.cleanupMediaQuery();
      this.productIdsDataMap = new Map();
      this.isSentViewedEventProductIdsSet = new Set();
      this.trackerService = null;
      this.initAttributes();
      this.showLoadingAndRemoveProductList();
      this.initObserver();
    }
  }

  initAttributes() {
    const dataProductIdsStr = this.getAttribute('data-product-ids');
    const allowEmptyProductIds = this.getAttribute(
      'data-allow-empty-product-ids',
    );
    this.allowEmptyProductIds = allowEmptyProductIds === 'true';
    this.productIds = dataProductIdsStr?.split(',') ?? null;
  }

  initObserver() {
    const hasProductIds = this.productIds?.length > 0;
    const shouldInitialize = hasProductIds || this.allowEmptyProductIds;
    if (shouldInitialize) {
      this.observer = intersectionObserver(
        (entry, observer) => {
          this.initRelatedProducts();
          observer.unobserve(this);
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      );
      this.observer?.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.productObserver?.disconnect();
    this.cleanupMediaQuery();
  }

  cleanupMediaQuery() {
    this.mediaQuery?.removeEventListener('change', this.mediaQueryListener);
    this.mediaQuery = null;
    this.mediaQueryListener = null;
  }

  initRelatedProducts() {
    return fetchRecommendProductListApi({
      productIds: this.productIds || [],
      pageType: pageType.CART_SIDEBAR,
    }).then((response) => {
      const { products, setting } = response ?? {};
      if (products) {
        const filterPriceEmptyProducts = products.filter((product) => {
          const { salePrice, regularPrice } = getProductPriceLabel({
            product,
            isLogin: !!window.mainConfig.currentUser,
            isPriceRangeEnabled:
              window.mainConfig.merchantData.price_range_preview ||
              window.mainConfig.merchantData.product_setting
                ?.price_range_enabled,
            hasMemberPrice:
              window.mainConfig.merchantData.rollout_keys.includes(
                'member_price',
              ),
          });
          const displayPrice = salePrice ?? regularPrice;
          return !!displayPrice;
        });
        // handle api cache and response value as { layout_style: { } }
        const layoutStyle = Object.keys(setting?.layout_style ?? {}).length
          ? setting.layout_style
          : defaultLayoutStyle;
        if (filterPriceEmptyProducts.length) {
          this.render({
            products: filterPriceEmptyProducts,
            layoutStyle,
          });
        } else {
          this.hideRelatedProducts();
        }
      }
    });
  }

  hideLoading() {
    const loadingElement = this.querySelector(`.${cartRelatedProductsLoading}`);
    if (loadingElement) {
      loadingElement.classList.add(cartRelatedProductsHide);
    }
  }

  hideRelatedProducts() {
    const relateProductElement = this.closest(`.${cartRelatedProducts}`);
    if (relateProductElement) {
      relateProductElement.classList.add(cartRelatedProductsHide);
    }
  }

  showLoadingAndRemoveProductList() {
    const relateProductElement = this.closest(`.${cartRelatedProducts}`);
    if (relateProductElement) {
      relateProductElement.classList.remove(cartRelatedProductsHide);
    }
    const loadingElement = this.querySelector(`.${cartRelatedProductsLoading}`);
    if (loadingElement) {
      loadingElement.classList.remove(cartRelatedProductsHide);
    }
    const productListElement = this.querySelector(
      `.${cartRelatedProductsList}`,
    );
    if (productListElement) {
      productListElement.innerHTML = '';
      productListElement.classList.remove(
        hasCartRelatedProductsHorizontalScroll,
      );
    }
  }

  getProductCardHTML(product) {
    const title = getTranslateModel(product.title_translations);
    const imageUrl = getMediaImageUrl(product.media?.[0], { size: '200x' });
    const { regularPrice, salePrice } = getProductPriceLabel({
      product,
      isLogin: !!window.mainConfig.currentUser,
      isPriceRangeEnabled:
        window.mainConfig.merchantData.price_range_preview ||
        window.mainConfig.merchantData.product_setting?.price_range_enabled,
      hasMemberPrice:
        window.mainConfig.merchantData.rollout_keys.includes('member_price'),
    });
    const priceDisplay = salePrice || regularPrice || '';
    const escapedTitle = title.replaceAll('"', '&quot;');

    return `
      <div data-e2e-id="${e2eIds.CONTENT}" class="cart-related-products-item" product-id="${product._id}">
        <a target="_blank" class="product-link js-product-link" href="/products/${product._id}">
          <div class="cart-related-products-item-image-container">
            <img
              data-e2e-id="${e2eIds.IMAGE}"
              class="cart-related-products-item-image"
              alt="${escapedTitle}"
              src="${imageUrl}"
              loading="lazy"
            />
          </div>
          <div class="cart-related-products-item-content">
            <div data-e2e-id="${e2eIds.TITLE}" class="cart-related-products-item-title">
              ${title}
            </div>
            <div data-e2e-id="${e2eIds.PRICE}" class="cart-related-products-item-price-details">
              ${priceDisplay}
            </div>
          </div>
        </a>
      </div>
    `;
  }

  getHTMLTemplate(products) {
    return products.map((product) => this.getProductCardHTML(product)).join('');
  }

  applyLayoutSetting({
    currentLayoutMode = layoutMode.FULL,
    productListElement,
  }) {
    if (currentLayoutMode === layoutMode.HORIZONTAL_SCROLL) {
      if (
        !productListElement.classList.contains(
          hasCartRelatedProductsHorizontalScroll,
        )
      ) {
        // wrap the product list with a horizontal scroll container
        const wrapper = document.createElement('div');
        wrapper.className = cartRelatedProductsHorizontalScroll;
        wrapper.innerHTML = productListElement.innerHTML;
        productListElement.innerHTML = '';
        productListElement.appendChild(wrapper);

        productListElement.classList.add(
          hasCartRelatedProductsHorizontalScroll,
        );
      }
    } else if (currentLayoutMode === layoutMode.FULL) {
      if (
        productListElement.classList.contains(
          hasCartRelatedProductsHorizontalScroll,
        )
      ) {
        // unwrap the horizontal scroll container if exists
        productListElement.classList.remove(
          hasCartRelatedProductsHorizontalScroll,
        );
        const wrapper = productListElement.querySelector(
          `.${cartRelatedProductsHorizontalScroll}`,
        );
        if (wrapper) {
          productListElement.innerHTML = wrapper.innerHTML;
        }
      }
    }
  }

  initLayoutSetting({ layoutStyle, productListElement }) {
    const mediaQueryList = window.matchMedia(
      `(max-width: ${deviceBreakpoint.MOBILE}px)`,
    );
    const device = mediaQueryList.matches
      ? deviceType.MOBILE
      : deviceType.DESKTOP;
    this.layoutSetting = layoutStyle[device];
    this.applyLayoutSetting({
      currentLayoutMode: this.layoutSetting,
      productListElement,
    });

    // add event listener for media query changes
    const listener = (event) => {
      const device = event.matches ? deviceType.MOBILE : deviceType.DESKTOP;
      this.layoutSetting = layoutStyle[device];
      this.applyLayoutSetting({
        currentLayoutMode: this.layoutSetting,
        productListElement,
      });
      // Re-observe product nodes since applyLayoutSetting rebuilds DOM via innerHTML
      this.initProductIntersectionObserver();
    };
    mediaQueryList.addEventListener('change', listener);
    this.mediaQuery = mediaQueryList;
    this.mediaQueryListener = listener;
  }

  setupProductIdsDataMap(products) {
    products.forEach((product, index) => {
      this.productIdsDataMap.set(product?._id, {
        product,
        order: index + 1,
      });
    });
  }

  initProductIntersectionObserver() {
    const targetNode = this.querySelector(`.${cartRelatedProductsList}`);
    if (!targetNode) return;

    if (this.productObserver) {
      this.productObserver.disconnect();
      this.productObserver = null;
    }

    this.productObserver = intersectionObserver(
      (entry, observer) => {
        this.trackProductVisible(entry.target);
        observer.unobserve(entry.target);
      },
      {
        threshold: 0.5,
      },
    );

    const productListNode = Array.from(
      targetNode.querySelectorAll('[product-id]'),
    );
    productListNode.forEach((node) => this.productObserver.observe(node));
  }

  trackProductVisible(element) {
    const productId = element?.getAttribute('product-id');
    if (!productId || this.isSentViewedEventProductIdsSet.has(productId))
      return;

    const { product, order } = this.productIdsDataMap.get(productId) || {};
    if (product && order) {
      sendViewRelatedProducts({
        product,
        order,
        basedProductIds: this.productIds,
        trackerService: this.trackerService,
        layoutSetting: this.layoutSetting,
      });
      sendProductImpression({
        trackerService: this.trackerService,
        productId: product._id,
      });
      this.isSentViewedEventProductIdsSet.add(productId);
    }
  }

  initProductClickEvent() {
    const targetNode = this.querySelector(`.${cartRelatedProductsList}`);
    if (!targetNode) return;

    targetNode.addEventListener('click', (e) => {
      const productElement = e.target.closest('[product-id]');
      const productId = productElement?.getAttribute('product-id');
      if (!productElement || !targetNode.contains(productElement)) return;

      const { product, order } = this.productIdsDataMap.get(productId) || {};
      if (product && order) {
        sendRecommendItem({
          productId: product._id,
          order,
          basedProductIds: this.productIds,
          recStrategy: product.rec_strategy,
          trackerService: this.trackerService,
          layoutSetting: this.layoutSetting,
        });
        sendProductClick({
          productId: product._id,
          trackerService: this.trackerService,
        });
      }
    });
  }

  async initEvent(products) {
    try {
      this.trackerService = await getNgTrackerService();
    } catch (error) {
      console.error('Failed to get tracker service:', error);
    }
    this.setupProductIdsDataMap(products);
    this.initProductIntersectionObserver();
    this.initProductClickEvent();
  }

  render({ products, layoutStyle }) {
    const targetNode = this.querySelector(`.${cartRelatedProductsList}`);
    if (targetNode) {
      targetNode.innerHTML = this.getHTMLTemplate(products);
      this.hideLoading();
      this.initEvent(products);
      this.initLayoutSetting({
        layoutStyle,
        productListElement: targetNode,
      });
    }
  }
}

if (!customElements.get('cart-lazyload-related-products')) {
  customElements.define(
    'cart-lazyload-related-products',
    CartLazyLoadRelatedProducts,
  );
}
